import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMappingsAgGridColumns, useMappingsMain } from '../../hooks';
import { IncidentMapping } from '../../models/Mapping';
import { MappingsAgGridTable, MappingsCardList } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection, CreateMappingButton, CatalogShortcuts, MobileActionsMenu } from '../interaction';
import { CreateMappingModal, CreateMappingModalRef, EditMappingModal, EditMappingModalRef } from '../modals';
import { useCouriers } from '../../hooks/useMappings';
import { isMobile } from 'react-device-detect';

export const Main = () => {
  const { t } = useTranslation('mappingsModule');
  const createModalRef = useRef<CreateMappingModalRef>(null);
  const editModalRef = useRef<EditMappingModalRef>(null);
  const { data: couriers = [] } = useCouriers();
  const couriersMap = useMemo(() => {
    const map = new Map<number, { id: number; name: string; image_hash?: string | null }>();
    couriers.forEach((c) => map.set(c.id, c));
    return map;
  }, [couriers]);
  const catalogShortcutsRef = useRef<any>(null);

  const {
    mappings,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    sorting,
    selectedColumns,
    mappingsViewProps,
    exportColumns,
    hasActiveFilters,
    fixedColumns,
    handleColumnsChange,
    handleSortingChange,
    courierId,
    status,
    handleCourierChange,
    handleStatusChange,
  } = useMappingsMain();

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const handleRowClick = useCallback(
    (mapping: IncidentMapping) => {
      editModalRef.current?.openWithMapping(mapping);
    },
    []
  );

  const { allColumnDefs, filteredColumnDefs, couriersLoaded } = useMappingsAgGridColumns({
    selectedColumns,
    onRowClick: handleRowClick,
  });

  // Convertir columnas AG Grid a formato para ColumnSelector
  // Usar allColumnDefs para que siempre incluya todas las columnas disponibles
  const allColumns = useMemo(() => {
    return allColumnDefs
      .filter((col) => {
        const field = col.field || '';
        // Excluir acciones y cualquier columna relacionada con "order"
        return field && field !== 'actions' && !field.toLowerCase().includes('order');
      })
      .map((col) => ({
        id: col.field || '',
        accessorKey: col.field || '',
        header: col.headerName || '',
      }));
  }, [allColumnDefs]);

  const renderMappings = () => {
    if (isMobile) {
      return (
        <MappingsCardList
          mappings={mappingsViewProps.mappings}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onEdit={handleRowClick}
          couriersMap={couriersMap}
        />
      );
    }

    return (
      <MappingsAgGridTable
        key={`mappings-table-${couriersLoaded ? 'loaded' : 'loading'}`}
        mappings={mappingsViewProps.mappings}
        columnDefs={filteredColumnDefs}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isFetching={isFetching}
        sorting={sorting}
        onSortingChange={handleSortingChange}
      />
    );
  };

  if (isInitialLoad) {
    return <LoadingState message={t('loading.message', 'Cargando mapeos...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              allColumns={allColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              fixedColumns={fixedColumns}
              exportColumns={exportColumns}
              courierId={courierId}
              status={status}
              onCourierChange={handleCourierChange}
              onStatusChange={handleStatusChange}
            />
          </div>
          <div>
            <TotalResults total={total} />
          </div>
          {/* Desktop actions */}
          <div className="shrink-0 absolute right-4 top-2 hidden md:flex items-center gap-2">
            <CatalogShortcuts ref={catalogShortcutsRef} hideButtons={false} />
            <CreateMappingButton onOpenModal={() => createModalRef.current?.openModal()} />
          </div>
          {/* Mobile actions dropdown */}
          <div className="shrink-0 absolute right-4 top-2 md:hidden">
            <MobileActionsMenu
              onOpenInternalCatalog={() => catalogShortcutsRef.current?.openInternalCatalog()}
              onOpenCourierCatalog={() => catalogShortcutsRef.current?.openCourierCatalog()}
              onOpenCreateMapping={() => createModalRef.current?.openModal()}
            />
          </div>
        </div>
      </div>

      {/* 🎯 SOLUCIÓN: Siempre renderizar AgGrid, pero con opacity 0 cuando no hay datos */}
      {!isInitialLoad && (
        <div className="relative mb-4 mt-4">
          {/* AgGrid siempre renderizado, con opacity 0 si no hay datos */}
          <div
            className={`transition-opacity duration-300 ${
              isFetching && !isFetchingNextPage
                ? 'opacity-50 pointer-events-none'
                : total > 0
                  ? 'opacity-100'
                  : 'opacity-0'
            }`}
          >
            {renderMappings()}
          </div>

          {/* EmptyView solo cuando no hay datos */}
          {total === 0 && (
            <div className="absolute inset-0">
              <EmptyView
                type={hasActiveFilters ? 'noResults' : 'emptyState'}
                icon={EMPTY_VIEW_ICONS.RULES}
              />
            </div>
          )}
        </div>
      )}

      {/* Modales */}
      <CreateMappingModal ref={createModalRef} />
      <EditMappingModal ref={editModalRef} />
    </div>
  );
};
